import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

interface UmamiWindow extends Window {
  umami?: {
    track: (eventName: string, eventData?: any) => void;
  };
}

declare let window: UmamiWindow;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const convertUnixTimestamp = (unixTimestamp: number | null) => {
  if (!unixTimestamp) {
    return "";
  }

  // Convert Unix timestamp to milliseconds
  const date = new Date(unixTimestamp * 1000);

  // Format the date and time
  const options = { year: "numeric", month: "short", day: "numeric" };
  const rawDate = new Date(date);

  // @ts-ignore
  return rawDate.toLocaleDateString("en-GB", options);
};

export const daysUntil = (timestampInSeconds: number) => {
  // Convert Unix timestamp to milliseconds
  let timestampInMilliseconds = timestampInSeconds * 1000;

  // Convert Unix timestamp to a Date object
  let targetDate = new Date(timestampInMilliseconds);

  // Get the current date
  let currentDate = new Date();

  // Calculate the difference in milliseconds
  // @ts-ignore todo fix this
  let differenceInMilliseconds = targetDate - currentDate;

  // Convert milliseconds to days
  return Math.ceil(differenceInMilliseconds / (1000 * 60 * 60 * 24));
};

export const formatDate = (date: Date | string, monthFormat: "short" | "long" = "long") => {
  if (!date) return;

  const formatted = new Date(date);

  const newFormatted = formatted?.toLocaleDateString("hr-HR", {
    day: "numeric",
    month: monthFormat,
    year: "numeric",
  });

  return newFormatted.replace(".", "");
};

export const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const getCityName = (
  locationString?: string | null,
  key: "name" | "display_name" = "name",
) => {
  if (!locationString) return;

  let cityName;

  try {
    cityName = JSON.parse(locationString)[key];
  } catch (e) {
    return "-";
  }

  return cityName || "-";
};

export const trackEvent = (eventName: string, eventData?: any) => {
  if (typeof window !== "undefined" && window.umami) {
    window.umami.track(eventName, eventData);
  }
};

export const cutString = (string: string, numberOfLetters: number) => {
  if (string.length > numberOfLetters) {
    return string.slice(0, numberOfLetters) + "..";
  }

  return string;
};

export const getDayNumber = (date: Date) => {
  if (!date) return;

  const formatted = new Date(date);

  const newFormatted = formatted?.toLocaleDateString("hr-HR", {
    day: "numeric",
  });

  if (newFormatted?.length === 2) return "0" + newFormatted;

  return newFormatted.replace(".", "");
};

export const getMonth = (date: Date) => {
  if (!date) return;

  const formatted = new Date(date);

  const newFormatted = formatted?.toLocaleDateString("hr-HR", {
    month: "long",
  });

  if (newFormatted?.length === 2) return "0" + newFormatted;

  return newFormatted;
};

const toPlainText = (html: string) =>
  html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// True when the short description is just the opening of the details, e.g. Facebook imports
// where it is cut from the first paragraph
export const isDescriptionInDetails = (description: string, blocks: any[] = []) => {
  const plainDescription = toPlainText(description).replace(/(\.\.\.|…)$/, "").trim();
  if (!plainDescription) return false;

  const plainDetails = toPlainText(
    blocks
      .map((block) =>
        block.type === "list" ? block.data?.items?.join(" ") : (block.data?.text ?? ""),
      )
      .join(" "),
  );

  return plainDetails.startsWith(plainDescription);
};

// Price form input: euros with up to two decimals, "10" or "12,50"
export const EVENT_PRICE_INPUT = /^\d{1,5}([.,]\d{1,2})?$/;

/** Price form input -> Event.price in euros. Null for free events, an empty field or 0. */
export const parseEventPrice = (price: string | null | undefined, isFree: boolean) => {
  const value = price?.trim();
  if (isFree || !value) return null;

  if (!EVENT_PRICE_INPUT.test(value))
    throw new Error("Cijena mora biti iznos u eurima, npr. 10 ili 12,50.");

  const amount = Number(value.replace(",", "."));
  return amount > 0 ? amount : null;
};

/** Event.price -> price form input (12.5 -> "12,50"). */
export const eventPriceToInput = (price: number | null | undefined) =>
  price == null ? "" : (Number.isInteger(price) ? String(price) : price.toFixed(2)).replace(".", ",");

/** 10 -> "10 €", 12.5 -> "12,50 €" */
export const formatEventPrice = (price: number) =>
  new Intl.NumberFormat("hr-HR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
  }).format(price);

/**
 * First euro amount in a free-text price ("8 € / 10 €" -> "8"), e.g. Facebook's ticket price.
 * Only numbers next to €/EUR count, so dates like "do 15.11." are never read as a price.
 */
export const getPriceAmount = (price: string | null | undefined) => {
  const match = price?.match(/(\d+(?:[.,]\d{1,2})?)\s*(?:€|eur)|(?:€|eur)\s*(\d+(?:[.,]\d{1,2})?)/i);
  const amount = match?.[1] ?? match?.[2];
  return amount ? amount.replace(",", ".") : null;
};
